import { useEffect, useState } from 'react';
import type { Session } from '../App';
import { Container, PageHeader } from '../App';

interface Props { session: Session; }

interface ZoneView {
  id: string;
  display_name: string;
  biome: string;
  connections: string[];
  discovered: boolean;
  is_current: boolean;
}

interface GatherNodeView {
  id: string;
  resource_ref: string;
  yield_min: number;
  yield_max: number;
  respawn_seconds: number;
}

export default function World({ session }: Props) {
  const playerId = session.playerId;
  const [zones, setZones] = useState<ZoneView[]>([]);
  const [currentZone, setCurrentZone] = useState<string | null>(null);
  const [nodes, setNodes] = useState<GatherNodeView[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [travelTarget, setTravelTarget] = useState('');

  const base = `/api/v1/games/${session.gameId}/modules/world`;

  const reloadZones = () => {
    fetch(`${base}/zones`)
      .then(r => r.json())
      .then((d: { zones: ZoneView[] }) => setZones(d.zones))
      .catch(e => setError(e.message));
  };

  const reloadPlayerZone = () => {
    fetch(`${base}/player/${playerId}/zone`)
      .then(r => r.json())
      .then((d: { zone: string | null }) => setCurrentZone(d.zone))
      .catch(e => setError(e.message));
  };

  const reloadNodes = () => {
    fetch(`${base}/player/${playerId}/nodes`)
      .then(r => r.json())
      .then((d: { nodes: GatherNodeView[] }) => setNodes(d.nodes))
      .catch(e => setError(e.message));
  };

  useEffect(() => { reloadZones(); reloadPlayerZone(); reloadNodes(); }, [session.gameId, playerId]);

  const travel = async (zoneId?: string) => {
    const target = zoneId || travelTarget.trim();
    if (!target) return;
    setBusy(true); setError(null);
    try {
      const res = await fetch(`${base}/player/${playerId}/travel`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ zone_id: target }),
      });
      if (!res.ok) throw new Error(await res.text());
      setTravelTarget('');
      reloadPlayerZone(); reloadNodes();
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  };

  const gather = async (nodeId: string) => {
    setBusy(true); setError(null);
    try {
      const res = await fetch(`${base}/player/${playerId}/gather`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ node_id: nodeId }),
      });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json() as { action: string; resource_ref: string; amount: number };
      setError(null);
      alert(`Gathered ${data.amount}x ${data.resource_ref}`);
      reloadNodes();
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  };

  return (
    <Container>
      <PageHeader
        title="World"
        subtitle="Zone browser, travel, and gathering."
      />
      {error && <div className="error-banner">{error}</div>}

      {/* Current Zone */}
      <div className="card mb-5 p-4">
        <h3 className="text-sm font-semibold mb-3">Player Location</h3>
        <div className="flex items-center gap-4">
          <div>
            <span className="text-xs text-fg-muted">Current Zone: </span>
            <span className="font-medium">{currentZone || 'None (not in any zone)'}</span>
          </div>
        </div>
      </div>

      {/* Travel */}
      <div className="card mb-5 p-4">
        <h3 className="text-sm font-semibold mb-3">Travel</h3>
        <div className="flex gap-3 items-end">
          <div>
            <label className="text-xs text-fg-muted block mb-1">Zone ID</label>
            <input className="input" placeholder="town" value={travelTarget} onChange={e => setTravelTarget(e.target.value)} />
          </div>
          <button className="btn btn-primary" disabled={busy || !travelTarget.trim()} onClick={() => travel()}>Travel</button>
        </div>
      </div>

      {/* Zone List */}
      {zones.length > 0 && (
        <div className="card mb-5">
          <h3 className="text-sm font-semibold p-4 pb-2">Zones</h3>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Name</th>
                  <th>Biome</th>
                  <th>Connections</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {zones.map(z => (
                  <tr key={z.id} className={z.is_current ? 'bg-brand/10' : ''}>
                    <td className="font-mono text-xs">{z.id}</td>
                    <td>{z.display_name}</td>
                    <td><span className="badge badge-muted">{z.biome || 'default'}</span></td>
                    <td className="text-xs">{z.connections.join(', ') || 'none'}</td>
                    <td>
                      <button
                        className="btn btn-sm"
                        disabled={busy || z.is_current}
                        onClick={() => travel(z.id)}
                      >
                        {z.is_current ? 'Here' : 'Travel'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {zones.length === 0 && (
        <div className="empty mb-5">
          <div className="text-fg-2 font-medium mb-1">No zones configured</div>
          <div className="text-xs text-fg-muted">Create zone definitions in Config Explorer, publish, then manage here.</div>
        </div>
      )}

      {/* Gather Nodes */}
      {nodes.length > 0 && (
        <div className="card">
          <h3 className="text-sm font-semibold p-4 pb-2">Gather Nodes (current zone)</h3>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Node ID</th>
                  <th>Resource</th>
                  <th>Yield</th>
                  <th>Respawn</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {nodes.map(n => (
                  <tr key={n.id}>
                    <td className="font-mono text-xs">{n.id}</td>
                    <td>{n.resource_ref}</td>
                    <td>{n.yield_min}-{n.yield_max}</td>
                    <td>{n.respawn_seconds}s</td>
                    <td>
                      <button className="btn btn-primary btn-sm" disabled={busy} onClick={() => gather(n.id)}>Gather</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {currentZone && nodes.length === 0 && (
        <div className="empty">
          <div className="text-fg-2 font-medium mb-1">No gather nodes</div>
          <div className="text-xs text-fg-muted">This zone has no gathering nodes configured.</div>
        </div>
      )}
    </Container>
  );
}
